import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { buildV3Location, getStableRouteKey } from '../locationUtils'
import { resolveGetComponent } from './routeLoaderCache'
import RouteLoadFallback from './RouteLoadFallback'

/**
 * Loads a v3 getComponent callback and renders the resolved component.
 */
export default function GetComponentRoute({ getComponent, routeScope }) {
  const location = useLocation()
  const params = useParams()
  const [Component, setComponent] = useState(null)
  const [error, setError] = useState(null)
  const getComponentRef = useRef(getComponent)
  getComponentRef.current = getComponent
  const routeKey = getStableRouteKey(location)
  const loadKey = `${routeScope ?? 'route'}:${routeKey}`
  const resolvedLoadKeyRef = useRef(null)
  const prevLoadKeyRef = useRef(null)
  const paramsKey = JSON.stringify(params)
  const v3Location = useMemo(() => buildV3Location(location, params), [routeKey, paramsKey])
  const v3LocationRef = useRef(v3Location)
  v3LocationRef.current = v3Location

  useEffect(() => {
    const loadComponent = getComponentRef.current
    if (!loadComponent) return undefined

    let cancelled = false
    setError(null)

    if (prevLoadKeyRef.current !== loadKey) {
      setComponent(null)
      resolvedLoadKeyRef.current = null
      prevLoadKeyRef.current = loadKey
    }

    resolveGetComponent(routeScope, routeKey, loadComponent, v3LocationRef.current)
      .then(component => {
        if (!cancelled) {
          resolvedLoadKeyRef.current = loadKey
          setComponent(() => component)
        }
      })
      .catch(err => {
        if (!cancelled) setError(err)
      })

    return () => {
      cancelled = true
    }
  }, [loadKey, routeKey, routeScope])

  if (error) throw error
  if (!Component || resolvedLoadKeyRef.current !== loadKey) {
    return <RouteLoadFallback />
  }

  return <Component location={v3Location} params={params} />
}

